import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import BeltFormDialog from '../../components/admin/BeltFormDialog';
import {
  ConfirmDialog,
  DataTable,
  InlineAlert,
  LoadingState,
  PageHeader,
  Toast,
} from '../../components/admin/ui';

const BeltsManagement = () => {
  const navigate = useNavigate();
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBelt, setSelectedBelt] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const fetchBelts = async () => {
    try {
      const response = await api.get('/admin/belts');
      setBelts(response.data.data);
    } catch (error) {
      console.error('Error fetching belts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBelts();
  }, []);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/belts/${confirmId}`);
      setConfirmId(null);
      setToast({ open: true, message: 'Cinturón eliminado', tone: 'success' });
      fetchBelts();
    } catch (error) {
      console.error('Error deleting belt:', error);
      setToast({ open: true, message: 'Error al eliminar el cinturón', tone: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState />;

  const columns = [
    {
      key: 'color',
      header: 'Color',
      render: (row) => (
        <span
          className="inline-block h-8 w-8 rounded-full border-2 border-line"
          style={{ backgroundColor: row.color_code || '#000' }}
        />
      ),
    },
    {
      key: 'name',
      header: 'Nombre',
      render: (row) => <span className="font-semibold">{row.name}</span>,
    },
    {
      key: 'hierarchy_order',
      header: 'Orden',
      render: (row) => (
        <span className="rounded-full border border-primary/30 px-2 py-0.5 text-xs font-semibold text-primary">
          Nivel {row.hierarchy_order}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Descripción',
      render: (row) => <span className="line-clamp-2 max-w-xs text-muted">{row.description || '—'}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-1">
          <button
            type="button"
            className="btn-admin-ghost !min-h-9"
            onClick={() => navigate(`/admin/belts/${row.id}/content`)}
          >
            Contenido
          </button>
          <button
            type="button"
            className="btn-admin-ghost !min-h-9"
            onClick={() => {
              setSelectedBelt(row);
              setDialogOpen(true);
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn-admin-ghost !min-h-9 text-danger"
            onClick={() => setConfirmId(row.id)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <PageHeader
        title="Cinturones"
        onBack={() => navigate('/admin')}
        action={
          <button
            type="button"
            className="btn-admin"
            onClick={() => {
              setSelectedBelt(null);
              setDialogOpen(true);
            }}
          >
            Agregar
          </button>
        }
      />

      <DataTable columns={columns} rows={belts} emptyTitle="No hay cinturones" />

      <InlineAlert tone="info" className="mt-6">
        La funcionalidad de documentos PDF se implementará en una fase posterior.
      </InlineAlert>

      <BeltFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedBelt(null);
        }}
        belt={selectedBelt}
        onSuccess={fetchBelts}
      />

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar cinturón"
        message="¿Estás seguro de eliminar este cinturón?"
        confirmLabel="Eliminar"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default BeltsManagement;
